/**
 * Print Profile Settings Component
 * 
 * Verwaltung von Druckprofilen für verschiedene Drucker-Rollen:
 * - Kassenbon (customer) - 80mm Standard
 * - Küche (kitchen) - 58mm Compact
 * - Bericht (report) - A4 Reports
 * 
 * Jedes Profil kann individuell konfiguriert werden:
 * - Papierformat
 * - Template-Auswahl
 * - Schriftgröße
 * - Logo/Footer-Anzeige
 * - OHNE-Hervorhebung
 */

import React, { useState, useEffect } from 'react';
import {
  Printer,
  Save,
  Eye,
  FileText,
  UtensilsCrossed,
  ClipboardList,
  Check,
  AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default Print Profiles
const DEFAULT_PROFILES = {
  customer: {
    paper_width: "80mm",
    template: "standard",
    font_size: "medium",
    show_logo: true,
    show_footer: true,
    highlight_ohne: true
  },
  kitchen: {
    paper_width: "58mm",
    template: "compact",
    font_size: "medium",
    show_logo: false,
    show_footer: false,
    highlight_ohne: true
  },
  report: {
    paper_width: "a4",
    template: "a4_report",
    font_size: "medium",
    show_logo: true,
    show_footer: true,
    highlight_ohne: false
  }
};

export default function PrintProfileSettings({ sessionToken }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState(DEFAULT_PROFILES);
  const [hasChanges, setHasChanges] = useState(false);

  // Lade Print Profiles beim Mount
  useEffect(() => {
    loadPrintProfiles();
  }, []);

  const loadPrintProfiles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenant/print-profiles`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.print_profiles && Object.keys(data.print_profiles).length > 0) {
          setProfiles(data.print_profiles);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Print Profiles:', error);
      toast.error('Druckprofile konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (role, field, value) => {
    setProfiles(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tenant/print-profiles`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profiles)
      });

      if (response.ok) {
        toast.success('Druckprofile erfolgreich gespeichert');
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Fehler:', error);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (role) => {
    toast.info('Vorschau wird geladen...', { duration: 1000 });
    // TODO: Öffne Vorschau-Modal oder neues Fenster
    console.log('Preview for role:', role, profiles[role]);
  };

  const ProfileEditor = ({ role, icon: Icon, title, description }) => {
    const profile = profiles[role];

    return (
      <Card data-testid={`print-profile-${role}`}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-blue-600" />
            <CardTitle>{title}</CardTitle>
          </div>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Papierformat */}
          <div className="space-y-2">
            <Label htmlFor={`${role}-paper`}>Papierformat</Label>
            <Select
              value={profile.paper_width}
              onValueChange={(value) => handleProfileChange(role, 'paper_width', value)}
            >
              <SelectTrigger id={`${role}-paper`} data-testid={`${role}-paper-width-select`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="58mm">58mm (Küchendrucker)</SelectItem>
                <SelectItem value="80mm">80mm (Standarddrucker)</SelectItem>
                <SelectItem value="a4">A4 (Berichte)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Template */}
          <div className="space-y-2">
            <Label htmlFor={`${role}-template`}>Layout-Template</Label>
            <Select
              value={profile.template}
              onValueChange={(value) => handleProfileChange(role, 'template', value)}
            >
              <SelectTrigger id={`${role}-template`} data-testid={`${role}-template-select`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (ausführlich)</SelectItem>
                <SelectItem value="compact">Compact (platzsparend)</SelectItem>
                <SelectItem value="a4_report">A4-Report (professionell)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Schriftgröße */}
          <div className="space-y-2">
            <Label htmlFor={`${role}-fontsize`}>Schriftgröße</Label>
            <Select
              value={profile.font_size}
              onValueChange={(value) => handleProfileChange(role, 'font_size', value)}
            >
              <SelectTrigger id={`${role}-fontsize`} data-testid={`${role}-font-size-select`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Klein (mehr Inhalt)</SelectItem>
                <SelectItem value="medium">Mittel (empfohlen)</SelectItem>
                <SelectItem value="large">Groß (besser lesbar)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggle-Optionen */}
          <div className="space-y-4 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor={`${role}-logo`}>Logo anzeigen</Label>
                <p className="text-xs text-gray-500">Firmenlogo auf Bon drucken</p>
              </div>
              <Switch
                id={`${role}-logo`}
                checked={profile.show_logo}
                onCheckedChange={(checked) => handleProfileChange(role, 'show_logo', checked)}
                data-testid={`${role}-show-logo-switch`}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor={`${role}-footer`}>Footer anzeigen</Label>
                <p className="text-xs text-gray-500">Abschiedstext am Ende des Bons</p>
              </div>
              <Switch
                id={`${role}-footer`}
                checked={profile.show_footer}
                onCheckedChange={(checked) => handleProfileChange(role, 'show_footer', checked)}
                data-testid={`${role}-show-footer-switch`}
              />
            </div>

            {role === 'kitchen' && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor={`${role}-ohne`}>"OHNE" hervorheben</Label>
                  <p className="text-xs text-gray-500">Entfernte Zutaten auffällig markieren</p>
                </div>
                <Switch
                  id={`${role}-ohne`}
                  checked={profile.highlight_ohne}
                  onCheckedChange={(checked) => handleProfileChange(role, 'highlight_ohne', checked)}
                  data-testid={`${role}-highlight-ohne-switch`}
                />
              </div>
            )}
          </div>

          {/* Vorschau Button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => handlePreview(role)}
            data-testid={`${role}-preview-button`}
          >
            <Eye className="w-4 h-4 mr-2" />
            Vorschau anzeigen
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="print-profile-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="w-6 h-6 text-blue-600" />
            Druckprofile
          </h2>
          <p className="text-gray-600 mt-1">
            Konfigurieren Sie die Druckeinstellungen für verschiedene Drucker-Typen
          </p>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="flex items-center gap-2"
          data-testid="save-print-profiles-button"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Speichern...
            </>
          ) : hasChanges ? (
            <>
              <Save className="w-4 h-4" />
              Änderungen speichern
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Gespeichert
            </>
          )}
        </Button>
      </div>

      {/* Info Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-900">Ungespeicherte Änderungen</p>
            <p className="text-sm text-yellow-700 mt-1">
              Sie haben Änderungen vorgenommen. Vergessen Sie nicht zu speichern!
            </p>
          </div>
        </div>
      )}

      {/* Tabs für verschiedene Profile */}
      <Tabs defaultValue="customer" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="customer" data-testid="tab-customer">
            <FileText className="w-4 h-4 mr-2" />
            Kassenbon
          </TabsTrigger>
          <TabsTrigger value="kitchen" data-testid="tab-kitchen">
            <UtensilsCrossed className="w-4 h-4 mr-2" />
            Küche
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">
            <ClipboardList className="w-4 h-4 mr-2" />
            Bericht
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customer" className="mt-6">
          <ProfileEditor
            role="customer"
            icon={FileText}
            title="Kassenbon (Kundendrucker)"
            description="Standard-Kassenbon für Kunden. Wird auf 80mm Thermopapier gedruckt."
          />
        </TabsContent>

        <TabsContent value="kitchen" className="mt-6">
          <ProfileEditor
            role="kitchen"
            icon={UtensilsCrossed}
            title="Küchenbon"
            description="Kompakter Bon für die Küche. Optimiert für 58mm Drucker mit Fokus auf Lesbarkeit."
          />
        </TabsContent>

        <TabsContent value="report" className="mt-6">
          <ProfileEditor
            role="report"
            icon={ClipboardList}
            title="Berichte (Z-Bericht, Tagesabschluss)"
            description="Professionelle A4-Reports für Tagesabschlüsse und Auswertungen."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
